/**
 * Bundle Step Picker
 * Dipdrip-style multi-step picker — customer selects one product per step
 * from a Shopify collection. On submit, adds ONE bundle product to cart with
 * the picks stored as line-item-properties.
 *
 * Required DOM: rendered by sections/bundle-step-picker.liquid
 * Cart endpoint: /cart/add.js
 */
(function () {
  'use strict';

  if (customElements.get('bundle-step-picker')) return;

  var CART_SECTION_IDS = ['cart-drawer', 'cart-icon-bubble'];

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  class BundleStepPicker extends HTMLElement {
    constructor() {
      super();

      this.bundleVariantId = parseInt(this.dataset.bundleVariantId, 10) || null;
      this.bundleProductId = parseInt(this.dataset.bundleProductId, 10) || null;
      this.stepsCount = parseInt(this.dataset.stepsCount, 10) || 0;
      this.ctaText = this.dataset.ctaText || 'Jetzt Bundle sichern';
      this.ctaIncompleteText = this.dataset.ctaIncompleteText || 'Wähle deine Produkte';
      this.errorText = this.dataset.errorText || 'Bundle konnte nicht hinzugefügt werden.';

      this.selections = {};
      this.activeStep = 0;
      this.steps = [];
      this.submitting = false;

      this.slotButtons = Array.from(this.querySelectorAll('[data-step-index]'));
      this.submitBtn = this.querySelector('[data-bsp-submit]');
      this.ctaTextEl = this.querySelector('[data-bsp-cta-text]');
      this.errorEl = this.querySelector('[data-bsp-error]');

      this.drawer = this.querySelector('[data-bsp-drawer]');
      this.drawerBackdrop = this.querySelector('[data-bsp-drawer-backdrop]');
      this.drawerClose = this.querySelector('[data-bsp-drawer-close]');
      this.drawerTitle = this.querySelector('[data-bsp-drawer-title]');
      this.drawerSubtitle = this.querySelector('[data-bsp-drawer-subtitle]');
      this.drawerGrid = this.querySelector('[data-bsp-drawer-grid]');
      this.drawerTabs = Array.from(this.querySelectorAll('[data-bsp-tab]'));
      this.drawerPrev = this.querySelector('[data-bsp-prev]');
      this.drawerNext = this.querySelector('[data-bsp-next]');
      this.drawerProgress = this.querySelector('[data-bsp-progress]');
      this.drawerCounter = this.querySelector('[data-bsp-counter]');
      this.productCardTpl = this.querySelector('[data-bsp-product-card-tpl]');
      this.emptyTpl = this.querySelector('[data-bsp-empty-tpl]');

      this.loadStepData();
      this.bindEvents();
      this.updateProgress();
    }

    loadStepData() {
      var scripts = this.querySelectorAll('[data-bsp-step-data]');
      scripts.forEach(function (el) {
        try {
          var data = JSON.parse(el.textContent.trim());
          this.steps[data.stepIndex] = data;
        } catch (e) {
          console.warn('[bundle-step-picker] could not parse step data', e);
        }
      }.bind(this));
    }

    bindEvents() {
      var self = this;

      this.slotButtons.forEach(function (btn) {
        btn.addEventListener('click', function (evt) {
          var resetEl = btn.querySelector('[data-bsp-reset]');
          if (resetEl && resetEl.contains(evt.target) && btn.dataset.filled === 'true') {
            evt.preventDefault();
            self.clearSlot(parseInt(btn.dataset.stepIndex, 10));
            return;
          }
          self.openDrawer(parseInt(btn.dataset.stepIndex, 10));
        });
      });

      this.drawerTabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          self.setActiveStep(parseInt(tab.dataset.bspTab, 10));
        });
      });

      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', function () { self.closeDrawer(); });
      }
      if (this.drawerBackdrop) {
        this.drawerBackdrop.addEventListener('click', function () { self.closeDrawer(); });
      }
      if (this.drawerPrev) {
        this.drawerPrev.addEventListener('click', function () {
          if (self.activeStep > 0) self.setActiveStep(self.activeStep - 1);
        });
      }
      if (this.drawerNext) {
        this.drawerNext.addEventListener('click', function () {
          if (self.activeStep < self.stepsCount - 1) {
            self.setActiveStep(self.activeStep + 1);
          } else if (self.isComplete()) {
            self.closeDrawer();
            self.submit();
          } else {
            var firstEmpty = self.firstEmptyStep();
            if (firstEmpty !== -1) self.setActiveStep(firstEmpty);
          }
        });
      }

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', function () { self.submit(); });
      }

      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && self.drawer && self.drawer.open) {
          self.closeDrawer();
        }
      });
    }

    firstEmptyStep() {
      for (var i = 0; i < this.stepsCount; i++) {
        if (!this.selections[i]) return i;
      }
      return -1;
    }

    isComplete() {
      for (var i = 0; i < this.stepsCount; i++) {
        if (!this.selections[i]) return false;
      }
      return true;
    }

    openDrawer(stepIndex) {
      this.setActiveStep(stepIndex);
      if (!this.drawer) return;
      if (typeof this.drawer.showModal === 'function') {
        try { this.drawer.showModal(); }
        catch (e) { this.drawer.setAttribute('open', ''); }
      } else {
        this.drawer.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
    }

    closeDrawer() {
      if (!this.drawer) return;
      if (typeof this.drawer.close === 'function') {
        try { this.drawer.close(); }
        catch (e) { this.drawer.removeAttribute('open'); }
      } else {
        this.drawer.removeAttribute('open');
      }
      document.body.style.overflow = '';
    }

    setActiveStep(stepIndex) {
      if (stepIndex < 0 || stepIndex >= this.stepsCount) return;
      this.activeStep = stepIndex;

      this.drawerTabs.forEach(function (tab) {
        var idx = parseInt(tab.dataset.bspTab, 10);
        tab.dataset.active = (idx === stepIndex) ? 'true' : 'false';
      });

      var step = this.steps[stepIndex];
      if (!step) return;

      if (this.drawerTitle) this.drawerTitle.textContent = step.drawerTitle || step.label;
      if (this.drawerSubtitle) {
        var remaining = this.stepsCount - Object.keys(this.selections).filter(function (k) { return this.selections[k]; }.bind(this)).length;
        if (remaining > 0) {
          this.drawerSubtitle.textContent = 'Füge noch ' + remaining + ' Produkt(e) hinzu, um dein Bundle zu sichern.';
        } else {
          this.drawerSubtitle.textContent = 'Alle Auswahlen getroffen.';
        }
      }

      this.renderGrid(stepIndex);
      this.updatePrevNextLabels();
    }

    updatePrevNextLabels() {
      if (this.drawerPrev) {
        this.drawerPrev.disabled = this.activeStep === 0;
      }
    }

    renderGrid(stepIndex) {
      var step = this.steps[stepIndex];
      if (!this.drawerGrid || !step) return;
      this.drawerGrid.innerHTML = '';

      if (!step.products || step.products.length === 0) {
        if (this.emptyTpl) {
          this.drawerGrid.appendChild(this.emptyTpl.content.cloneNode(true));
        }
        return;
      }

      var selected = this.selections[stepIndex];
      var self = this;
      step.products.forEach(function (product) {
        if (!self.productCardTpl) return;
        var node = self.productCardTpl.content.cloneNode(true);
        var card = node.querySelector('.bsp-product-card');
        var img = node.querySelector('img');
        var titleEl = node.querySelector('.bsp-product-card__title');
        var pickBtn = node.querySelector('[data-bsp-pick]');

        card.dataset.productId = product.id;
        card.dataset.variantId = product.variantId;
        if (selected && selected.variantId === product.variantId) {
          card.dataset.selected = 'true';
        }

        if (img) {
          img.src = product.image || '';
          img.alt = product.title;
        }
        if (titleEl) titleEl.textContent = product.title;

        var pick = function () { self.handlePick(stepIndex, product); };
        if (card) {
          card.addEventListener('click', pick);
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.addEventListener('keydown', function (evt) {
            if (evt.key === 'Enter' || evt.key === ' ') {
              evt.preventDefault();
              pick();
            }
          });
        }
        if (pickBtn) {
          pickBtn.addEventListener('click', function (evt) {
            evt.stopPropagation();
            pick();
          });
        }

        self.drawerGrid.appendChild(node);
      });
    }

    handlePick(stepIndex, product) {
      this.selections[stepIndex] = {
        productId: product.id,
        variantId: product.variantId,
        title: product.title,
        image: product.image,
        url: product.url,
        propertyName: this.steps[stepIndex].propertyName
      };

      this.updateSlot(stepIndex);
      this.updateProgress();

      var nextEmpty = this.firstEmptyStep();
      if (nextEmpty === -1) {
        this.closeDrawer();
      } else {
        this.setActiveStep(nextEmpty);
      }
    }

    clearSlot(stepIndex) {
      delete this.selections[stepIndex];
      this.updateSlot(stepIndex);
      this.updateProgress();
    }

    updateSlot(stepIndex) {
      var slotBtn = this.slotButtons.find(function (b) {
        return parseInt(b.dataset.stepIndex, 10) === stepIndex;
      });
      if (!slotBtn) return;

      var selection = this.selections[stepIndex];
      var previewEl = slotBtn.querySelector('[data-bsp-preview]');
      var labelEl = slotBtn.querySelector('[data-bsp-label]');

      if (selection) {
        slotBtn.dataset.filled = 'true';
        if (previewEl) {
          previewEl.innerHTML = '';
          if (selection.image) {
            var img = document.createElement('img');
            img.src = selection.image;
            img.alt = selection.title;
            img.loading = 'lazy';
            previewEl.appendChild(img);
          }
        }
        if (labelEl) labelEl.textContent = selection.title;
      } else {
        slotBtn.dataset.filled = 'false';
        if (previewEl) {
          previewEl.innerHTML = '<span class="bsp-slot__plus" aria-hidden="true">+</span>';
        }
        if (labelEl) labelEl.textContent = '';
      }
    }

    updateProgress() {
      var filled = Object.keys(this.selections).filter(function (k) {
        return this.selections[k];
      }.bind(this)).length;

      var percent = this.stepsCount > 0 ? (filled / this.stepsCount) * 100 : 0;
      if (this.drawerProgress) this.drawerProgress.style.width = percent + '%';
      if (this.drawerCounter) this.drawerCounter.textContent = filled;

      if (this.submitBtn) {
        this.submitBtn.disabled = !this.isComplete();
      }
      if (this.ctaTextEl) {
        this.ctaTextEl.textContent = this.isComplete() ? this.ctaText : this.ctaIncompleteText;
      }
    }

    buildProperties() {
      var properties = {};
      var self = this;
      Object.keys(this.selections).forEach(function (key) {
        var sel = self.selections[key];
        if (!sel) return;
        var propKey = sel.propertyName || ('Auswahl ' + (parseInt(key, 10) + 1));
        properties[propKey] = sel.title;
        properties['_' + propKey + '_variant'] = String(sel.variantId);
      });
      return properties;
    }

    async submit() {
      if (this.submitting) return;
      if (!this.bundleVariantId) {
        this.showError(this.errorText);
        return;
      }
      if (!this.isComplete()) {
        var first = this.firstEmptyStep();
        if (first !== -1) this.openDrawer(first);
        return;
      }

      this.submitting = true;
      this.submitBtn.disabled = true;
      this.clearError();

      var body = {
        items: [{
          id: this.bundleVariantId,
          quantity: 1,
          properties: this.buildProperties()
        }],
        sections: CART_SECTION_IDS.join(','),
        sections_url: window.location.pathname
      };

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          throw new Error(err.description || ('HTTP ' + res.status));
        }

        var result = await res.json();

        if (result && result.sections) {
          this.applyCartSections(result.sections);
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'bundle-step-picker' }
        }));
        if (typeof window.updateCart === 'function') window.updateCart();

        this.openCartDrawer();
      } catch (e) {
        this.showError(e.message || this.errorText);
      } finally {
        this.submitting = false;
        this.submitBtn.disabled = !this.isComplete();
      }
    }

    applyCartSections(sections) {
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');

        if (id === 'cart-drawer') {
          var newInner = parsed.querySelector('.drawer__inner');
          var currentInner = document.querySelector('cart-drawer .drawer__inner');
          if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
          var drawerEl = document.querySelector('cart-drawer');
          if (drawerEl) drawerEl.classList.remove('is-empty');
        }

        if (id === 'cart-icon-bubble') {
          var newBubble = parsed.querySelector('.cart-count-bubble');
          var currentBubble = document.querySelector('.cart-count-bubble');
          var bubbleHost = document.getElementById('cart-icon-bubble');
          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          } else if (newBubble && bubbleHost) {
            bubbleHost.appendChild(newBubble);
          } else if (!newBubble && currentBubble) {
            currentBubble.remove();
          }
        }
      });
    }

    openCartDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      if (drawer) {
        drawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
        return;
      }
      // Final fallback: redirect to /cart
      window.location.href = '/cart';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  customElements.define('bundle-step-picker', BundleStepPicker);
})();
